import React from 'react';

const SESSION_EVENT = 'session-storage-change';

export function setSessionStorageWithEvent(key: string, value: unknown) {
  sessionStorage.setItem(key, JSON.stringify(value));
  window.dispatchEvent(new CustomEvent(SESSION_EVENT, { detail: { key } }));
}

function safeParse<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function useSessionStorageListener<T>(key: string | null) {
  const [value, setValue] = React.useState<T | null>(() => {
    if (!key) return null;
    return safeParse<T>(sessionStorage.getItem(key));
  });

  React.useEffect(() => {
    if (!key) return;

    const read = () => setValue(safeParse<T>(sessionStorage.getItem(key)));

    read();

    const onCustom = (e: Event) => {
      const k = (e as CustomEvent<{ key: string }>).detail?.key;
      if (k === key) read();
    };

    const onStorage = (e: StorageEvent) => {
      if (e.storageArea !== sessionStorage) return;
      if (e.key === key) read();
    };

    window.addEventListener(SESSION_EVENT, onCustom);
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener(SESSION_EVENT, onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, [key]);

  return value;
}
